import { useEffect, useMemo } from "react";
import { useMoralis, useWeb3Contract } from "react-moralis";
import { useInvestment } from "./useInvestment";
import { useMulticall } from "./useMulticall";
import { resCompiler } from "helpers/responseCompiler";
import { useMainController } from "context";
import structCompiler from "helpers/structCompiler";

const evn = {
  event: ["GiftReceived", "ReferralRecieved"],
  field: [
    ["value", "block_timestamp", "transaction_hash"],
    ["from", "value", "block_timestamp", "transaction_hash"],
  ],
};

const txs = {
  event: [
    "WithdrawInterest",
    "WithdrawToInvest",
    "Invest3Month",
    "Invest6Month",
    "InvestFree",
    "WithdrawInvest",
    "UpgradeToMonthly",
  ],
  field: [
    ["gift", "referrals", "hourly", "block_timestamp", "transaction_hash"],
    ["gift", "referrals", "hourly", "block_timestamp", "transaction_hash"],
    ["referrer", "value", "block_timestamp", "transaction_hash"],
    ["referrer", "value", "block_timestamp", "transaction_hash"],
    ["value", "block_timestamp", "transaction_hash"],
    ["value", "block_timestamp", "transaction_hash"],
    ["value", "block_timestamp", "transaction_hash"],
  ],
};

export const useUpdater = () => {
  const { Moralis, account, chainId } = useMoralis();
  const { address: investContract, abi: investAbi } = useInvestment();
  const { address: multicallContract, abi: multicallAbi } = useMulticall();
  const { runContractFunction, isFetching, isLoading } = useWeb3Contract();
  const [{ update }, dispatch] = useMainController();

  useEffect(() => {
    if (!account) return;
    console.log("User Changed: ", account);
    dispatch({ type: "USER_CHANGED" });
  }, [dispatch, account]);

  let iface = useMemo(
    () => (Moralis.web3Library ? new Moralis.web3Library.utils.Interface(investAbi) : null),
    [Moralis.web3Library, investAbi]
  );

  const txQueries = useMemo(() => {
    return txs.event.map((event) => new Moralis.Query(Moralis.Object.extend(event)));
  }, [Moralis]);

  const evQueries = useMemo(() => {
    return evn.event.map((event) => new Moralis.Query(Moralis.Object.extend(event)));
  }, [Moralis]);

  useEffect(() => {
    if (!account || !iface || !update) return;
    txQueries.map((query) => query.equalTo("user", account));
    evQueries.map((query) => query.equalTo("user", account));

    dispatch({ type: "START_UPDATE" });
    let callCounter = 0;

    const multiCalls = {
      address: investContract,
      methods: [
        "BNBPrice",
        "BNBValue",
        "userInvestDetails",
        "users",
        "userMaxMonth",
        "calculateInterest",
      ],
      params: [[], [account], [account], [account], [account], [account]],
    };

    const calls = multiCalls.methods.reduce(
      (items, method, i) => [
        ...items,
        {
          target: multiCalls.address.toLowerCase(),
          callData: iface.encodeFunctionData(method, multiCalls.params[i]),
        },
      ],
      []
    );

    const onSuccess = ({ returnData }) => {
      const invest = multiCalls.methods.reduce(
        (items, method) => {
          const compiled = resCompiler(
            iface.decodeFunctionResult(method, returnData[callCounter++]),
            iface.getFunction(method).outputs
          );
          if (method === "userInvestDetails") {
            if (!compiled) return items;
            const userInvestDetails = structCompiler(compiled);
            return {
              ...items,
              userInvestDetails,
            };
          } else
            return {
              ...items,
              [method]: compiled,
            };
        },
        { user: account, chainId }
      );
      invest.transactions = [];
      invest.events = [];

      Promise.all([
        Promise.all(txQueries.map((query) => query.find())),
        Promise.all(evQueries.map((query) => query.find())),
      ])
        .then(([tx, ev]) => {
          tx.forEach((result, i) => {
            if (result[0])
              invest.transactions[result[0].className] = result.reduce(
                (alls, res) => [
                  ...alls,
                  txs.field[i].reduce((all, item) => ({ ...all, [item]: res.get(item) }), {}),
                ],
                []
              );
          });
          ev.forEach((result, i) => {
            if (result[0])
              invest.events[result[0].className] = result.reduce(
                (alls, res) => [
                  ...alls,
                  evn.field[i].reduce((all, item) => ({ ...all, [item]: res.get(item) }), {}),
                ],
                []
              );
          });

          dispatch({ type: "UPDATE_INVEST", invest });
        })
        .catch((err) => {
          console.log(err);
          dispatch({ type: "UPDATE_INVEST", invest });
        });
    };

    runContractFunction({
      params: {
        contractAddress: multicallContract,
        functionName: "aggregate",
        abi: multicallAbi,
        params: { calls },
      },
      onSuccess,
      onError: (err) => console.error(err),
    });
  }, [
    chainId,
    account,
    dispatch,
    update,
    iface,
    txQueries,
    investAbi,
    investContract,
    multicallAbi,
    multicallContract,
    runContractFunction,
    evQueries,
  ]);

  return { isFetching, isLoading };
};
